import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {BdManualContractDecisionService} from '../../../service/api/bd-manual-contract-decision.service';
import {
    BdRateAgreementInstallment,
    BdRatePlan,
    computeRatePlan
} from '../../../util/bd-rate-plan.util';

/**
 * Per-contract decision row state used inside the table.
 */
interface BdContractDecisionRow {
    /** Foreign key of the dunning contract. */
    contractId: number;
    /** Display name of the contract (identifier / display label). */
    contractName: string;
    /** Outstanding dunning balance for the contract. */
    dunningBalance: number;
    /** True for code-ranges 11101-11104 (Vermittler-Hinweis). */
    agentHint: boolean;
    /** Vermittlernummer (Klartext, C19) — null wenn nicht vorhanden. */
    agentNumber: string | null;
    /** True for LV contracts (Lebensversicherung, C19). */
    isLvContract: boolean;
    /** Currently selected decision option (null until user picks one). */
    selectedAction: BdContractDecisionAction | null;
    /** True while a request is in-flight for this row. */
    submitting: boolean;
    /** True once the row has been successfully submitted. */
    submitted: boolean;
    /** Optional rate-agreement parameters when the user picks `RATE_AGREEMENT`. */
    rateAgreement: {
        totalAmount: number | null;
        monthlyAmount: number | null;
        monthsCount: number | null;
        showDialog: boolean;
    };
    /** Optional write-off amount + reason. */
    writeOff: {
        amount: number | null;
        reason: string | null;
    };
    /**
     * G5: Optionales Stichtag-Datum der Wiederinkraftsetzung (REACTIVATE).
     * {@code null} = unbefristet (REACTIVATE_UNTIL entfällt).
     */
    reactivateEffectiveDate: Date | null;
    /**
     * G6: Pflicht-Stichtag des Stornos (STORNO). Solange {@code null}, bleibt der
     * Submit-Button für STORNO deaktiviert.
     */
    stornoEffectiveDate: Date | null;
    /** Free-text reason / comment that accompanies the submission. */
    reason: string;
}

/**
 * Decision options exposed via the per-row dropdown.
 *
 * G5/G6: 'REACTIVATE' (Wiederinkraftsetzung, reversibel) und 'STORNO'
 * (eigenständiger Storno, irreversibel) sind jetzt eigene Aktionen mit
 * dedizierten Backend-Endpunkten ({@code /reactivate} bzw. {@code /storno}).
 * STORNO wird NICHT mehr in den HANDOVER-Branch geroutet, sondern an seinen
 * eigenen Endpunkt gesendet.
 */
export type BdContractDecisionAction =
    | 'HANDOVER'
    | 'WRITE_OFF'
    | 'RATE_AGREEMENT'
    | 'SPECIAL_HANDLING'
    | 'REACTIVATE'
    | 'STORNO';

interface DecisionOption {
    label: string;
    value: BdContractDecisionAction;
}

/**
 * Pending confirmation payload shown in the irreversible-action confirm dialog
 * (C05). Captured when the user presses "Auswahl bestätigen"; the actual POST
 * is only fired after the user confirms in the dialog.
 *
 * @ticket TM-Wave-B
 */
interface BdPendingConfirmation {
    row: BdContractDecisionRow;
    action: BdContractDecisionAction;
    actionLabel: string;
    contractName: string;
    amount: number;
    reason: string;
}

/**
 * AI:
 * Status: 'wave-3-implementation'
 * Type: Component
 * SubType: Exit-UI / Decision-Table
 * Reason: Renders a PrimeNG p-table that lists every dunning contract with a per-row
 *         decision dropdown (Storno / Übergabe / Abschreibung / Ratenzahlung /
 *         Sonderbehandlung) and a submit button. Selected outcome is forwarded to the
 *         appropriate backend endpoint via {@link BdManualContractDecisionService}.
 *
 *         Mapped to {@code BdDunningExitUiComponent.CONTRACT_DECISION_TABLE}.
 *
 * @ticket TM-1000001786
 */
@Component({
    selector: 'bd-contract-decision-table',
    templateUrl: './bd-contract-decision-table.component.html',
    styleUrls: ['./bd-contract-decision-table.component.scss'],
    standalone: false
})
export class BdContractDecisionTableComponent implements OnInit {

    // -------------------------------------------------------------------- //
    // Inputs
    // -------------------------------------------------------------------- //

    /**
     * Raw dunning row passed by the parent drawer. Used to read the dunningId
     * and any contextual fields.
     */
    @Input() dunning: Record<string, any> | null = null;

    /**
     * Optional list of dunning contracts (already enriched with balance + agentHint).
     * Wave 4 will populate this from a QL query; Wave 3 accepts the raw list and falls
     * back to a small stub when nothing is provided so the UI is testable end-to-end.
     */
    @Input() contracts: any[] | null = null;

    /**
     * Parsed uiComponentConfig payload. Currently informational only.
     */
    @Input() uiComponentConfig: Record<string, any> | null = null;

    /**
     * U17: Id der Mahnung — vom Drawer durchgereicht. Wird hier nicht direkt
     * genutzt (dunningId wird aus {@link dunning} gelesen), MUSS aber deklariert
     * werden, damit die Drawer-Bindung kein NG0303 auslöst.
     */
    @Input() dunningId: number | null = null;

    /** U17: Inkasso-Übergaben der Mahnung (vom Drawer durchgereicht). */
    @Input() handOvers: unknown[] | null = null;

    /** U17: Aktive Mahn-Pause, sofern vorhanden (vom Drawer durchgereicht). */
    @Input() activePause: unknown | null = null;

    /** U17: Gebrochener Zahlungsplan, sofern vorhanden (vom Drawer durchgereicht). */
    @Input() breachedPlan: unknown | null = null;

    // -------------------------------------------------------------------- //
    // Outputs
    // -------------------------------------------------------------------- //

    /** Emitted once all rows have been submitted successfully (drawer can close). */
    @Output() actionSubmitted = new EventEmitter<{contractId: number; action: BdContractDecisionAction}>();

    // -------------------------------------------------------------------- //
    // State
    // -------------------------------------------------------------------- //

    initialized: boolean = false;

    /**
     * Selectable options shown in the per-row dropdown.
     *
     * G5/G6: 'Vertrag wieder in Kraft setzen' (reversibel) und 'Vertrag
     * stornieren' (irreversibel) sind ergänzt; beide rufen dedizierte Endpunkte.
     */
    readonly decisionOptions: DecisionOption[] = [
        {label: 'Inkasso-Übergabe',              value: 'HANDOVER'},
        {label: 'Abschreibung',                  value: 'WRITE_OFF'},
        {label: 'Ratenzahlung',                  value: 'RATE_AGREEMENT'},
        {label: 'Sonderbehandlung',              value: 'SPECIAL_HANDLING'},
        {label: 'Vertrag wieder in Kraft setzen', value: 'REACTIVATE'},
        {label: 'Vertrag stornieren',            value: 'STORNO'}
    ];

    /**
     * Actions that are irreversible and therefore require BOTH a mandatory
     * free-text reason AND an explicit confirmation dialog (C05). Ratenzahlung
     * is reversible (a plan can be cancelled) and is excluded; G5 REACTIVATE is
     * likewise reversible. G6 STORNO is irreversible and therefore included.
     *
     * @ticket TM-Wave-B
     */
    private readonly irreversibleActions: ReadonlySet<BdContractDecisionAction> =
        new Set<BdContractDecisionAction>(['HANDOVER', 'WRITE_OFF', 'SPECIAL_HANDLING', 'STORNO']);

    rows: BdContractDecisionRow[] = [];

    /** Error message shown above the table for failed submissions. */
    errorMessage: string | null = null;

    /**
     * Current user id, sent as submittedByUserId of the hand-over (passed through
     * by the drawer). TM-1000002054 (K-14): the backend ignores it and always takes
     * the submitter from the authenticated principal.
     *
     * @ticket TM-Wave-B
     */
    @Input() currentUserId: number | null = null;

    /**
     * Pending confirmation for an irreversible action (C05). Non-null while the
     * confirm dialog is open; the POST fires only after {@link confirmPending}.
     *
     * @ticket TM-Wave-B
     */
    pendingConfirmation: BdPendingConfirmation | null = null;

    constructor(private readonly manualContractDecisionService: BdManualContractDecisionService) {}

    // -------------------------------------------------------------------- //
    // Lifecycle
    // -------------------------------------------------------------------- //

    ngOnInit(): void {
        this.rows = this.buildRows(this.contracts);
        this.initialized = true;
    }

    // -------------------------------------------------------------------- //
    // Row interactions
    // -------------------------------------------------------------------- //

    /**
     * Opens the rate-agreement sub-dialog when the user selects RATE_AGREEMENT
     * for a row. For all other options the sub-dialog stays closed.
     */
    onDecisionChange(row: BdContractDecisionRow): void {
        row.rateAgreement.showDialog = row.selectedAction === 'RATE_AGREEMENT';
        // Reset write-off fields when the user switches away from WRITE_OFF.
        if (row.selectedAction !== 'WRITE_OFF') {
            row.writeOff = {amount: null, reason: null};
        }
        // G5: Stichtag der Wiederinkraftsetzung zurücksetzen, wenn nicht mehr aktiv.
        if (row.selectedAction !== 'REACTIVATE') {
            row.reactivateEffectiveDate = null;
        }
        // G6: Storno-Stichtag zurücksetzen, wenn nicht mehr aktiv.
        if (row.selectedAction !== 'STORNO') {
            row.stornoEffectiveDate = null;
        }
    }

    /**
     * Returns true when the given action requires a mandatory free-text reason
     * AND a confirmation dialog before it is sent (C05).
     *
     * @ticket TM-Wave-B
     */
    isIrreversible(action: BdContractDecisionAction | null): boolean {
        return action != null && this.irreversibleActions.has(action);
    }

    /**
     * Resolves the effective reason for a row. WRITE_OFF carries its own reason
     * field; all other actions use the shared {@link BdContractDecisionRow.reason}.
     *
     * @ticket TM-Wave-B
     */
    private effectiveReason(row: BdContractDecisionRow): string {
        const raw = row.selectedAction === 'WRITE_OFF'
            ? (row.writeOff.reason ?? '')
            : (row.reason ?? '');
        return raw.trim();
    }

    /**
     * Entry point from the "Auswahl bestätigen" button. For irreversible actions
     * (HANDOVER/WRITE_OFF/SPECIAL_HANDLING) it opens a confirmation dialog with a
     * plain-DE summary (Vertrag, Betrag, Aktion, Begründung) instead of POSTing
     * immediately (C05). Reversible actions (Ratenzahlung) are submitted directly.
     *
     * @ticket TM-Wave-B
     */
    submitRow(row: BdContractDecisionRow): void {
        const dunningId = this.dunning?.['id'] ?? this.dunning?.['dunningId'];
        if (dunningId == null || row.selectedAction == null) {
            this.errorMessage = 'Bitte Mahnung und Aktion auswählen.';
            return;
        }
        this.errorMessage = null;

        if (this.isIrreversible(row.selectedAction)) {
            const reason = this.effectiveReason(row);
            if (reason.length === 0) {
                this.errorMessage = 'Bitte eine Begründung angeben.';
                return;
            }
            // G6: STORNO erfordert einen Pflicht-Stichtag (effectiveDate).
            if (row.selectedAction === 'STORNO' && row.stornoEffectiveDate == null) {
                this.errorMessage = 'Bitte einen Stichtag für den Storno angeben.';
                return;
            }
            const amount = row.selectedAction === 'WRITE_OFF'
                ? (row.writeOff.amount ?? row.dunningBalance ?? 0)
                : row.dunningBalance ?? 0;
            this.pendingConfirmation = {
                row,
                action: row.selectedAction,
                actionLabel: this.getActionLabel(row.selectedAction),
                contractName: row.contractName,
                amount,
                reason
            };
            return;
        }

        // Reversible action — execute directly (no confirm dialog).
        this.executeRow(row);
    }

    /**
     * Confirms the pending irreversible action (C05) and fires the actual POST.
     *
     * @ticket TM-Wave-B
     */
    confirmPending(): void {
        const pending = this.pendingConfirmation;
        this.pendingConfirmation = null;
        if (pending) {
            this.executeRow(pending.row);
        }
    }

    /**
     * Dismisses the pending confirmation without sending anything (C05).
     *
     * @ticket TM-Wave-B
     */
    cancelPending(): void {
        this.pendingConfirmation = null;
    }

    /**
     * Performs the backend call for the selected action. No system-generated
     * default reasons are used: irreversible actions always carry the user's
     * mandatory free-text reason (validated in {@link submitRow}).
     *
     * @ticket TM-Wave-B
     */
    private executeRow(row: BdContractDecisionRow): void {
        const dunningId = this.dunning?.['id'] ?? this.dunning?.['dunningId'];
        if (dunningId == null || row.selectedAction == null) {
            this.errorMessage = 'Bitte Mahnung und Aktion auswählen.';
            return;
        }
        row.submitting = true;

        const completed = () => {
            row.submitting = false;
            row.submitted = true;
            this.actionSubmitted.emit({
                contractId: row.contractId,
                action: row.selectedAction!
            });
        };
        const failed = (err: unknown) => {
            row.submitting = false;
            this.errorMessage = this.extractError(err);
        };

        const reason = this.effectiveReason(row);

        switch (row.selectedAction) {
            case 'HANDOVER':
                this.manualContractDecisionService.submitHandOver(
                    dunningId,
                    row.contractId,
                    'HANDOVER',
                    reason,
                    this.currentUserId ?? undefined
                ).subscribe({next: completed, error: failed});
                break;

            case 'WRITE_OFF': {
                const amount = row.writeOff.amount ?? row.dunningBalance ?? 0;
                this.manualContractDecisionService.executeWriteOff(
                    dunningId,
                    row.contractId,
                    amount,
                    reason,
                    this.currentUserId ?? undefined
                ).subscribe({next: completed, error: failed});
                break;
            }

            case 'SPECIAL_HANDLING':
                this.manualContractDecisionService.executeSpecialHandling(
                    dunningId,
                    row.contractId,
                    reason,
                    this.currentUserId ?? undefined
                ).subscribe({next: completed, error: failed});
                break;

            case 'RATE_AGREEMENT': {
                // G10: Cent-genauen Plan aus dem geteilten Util berechnen. Es wird
                // weiterhin der bestehende Pro-Vertrag-Endpunkt verwendet:
                // monthlyAmount = berechneter rateAmount, monthsCount = count.
                const total = this.ratePlanTotal(row);
                const months = row.rateAgreement.monthsCount ?? 0;
                if (months <= 0) {
                    row.submitting = false;
                    this.errorMessage = 'Bitte die Laufzeit (Anzahl Monate) angeben.';
                    return;
                }
                const plan = this.ratePlan(row);
                if (plan.installments.length === 0) {
                    row.submitting = false;
                    this.errorMessage = 'Kein gültiger Ratenplan berechenbar. Bitte Eingaben prüfen.';
                    return;
                }
                this.manualContractDecisionService.executeRateAgreement(
                    dunningId,
                    row.contractId,
                    total,
                    plan.rateAmount,
                    months,
                    reason
                ).subscribe({next: completed, error: failed});
                break;
            }

            case 'REACTIVATE': {
                // G5: Reversible Wiederinkraftsetzung am dedizierten Endpunkt.
                // null effectiveDate = unbefristet; gesetzt = REACTIVATE_UNTIL.
                const effectiveDate = row.reactivateEffectiveDate
                    ? this.toIsoDate(row.reactivateEffectiveDate)
                    : null;
                this.manualContractDecisionService.executeReactivate(
                    dunningId,
                    row.contractId,
                    effectiveDate,
                    reason,
                    this.currentUserId ?? undefined
                ).subscribe({next: completed, error: failed});
                break;
            }

            case 'STORNO': {
                // G6: Irreversibler Storno am dedizierten /storno-Endpunkt
                // (NICHT über den HANDOVER-Branch). Stichtag ist Pflicht.
                if (row.stornoEffectiveDate == null) {
                    row.submitting = false;
                    this.errorMessage = 'Bitte einen Stichtag für den Storno angeben.';
                    return;
                }
                this.manualContractDecisionService.executeStorno(
                    dunningId,
                    row.contractId,
                    this.toIsoDate(row.stornoEffectiveDate),
                    reason,
                    this.currentUserId ?? undefined
                ).subscribe({next: completed, error: failed});
                break;
            }
        }
    }

    /**
     * G10: Gesamtbetrag der Ratenplanung der Zeile (Vorgabe-Betrag bzw. Mahn-Saldo).
     */
    ratePlanTotal(row: BdContractDecisionRow): number {
        return row.rateAgreement.totalAmount ?? row.dunningBalance ?? 0;
    }

    /**
     * G10: Berechnet den cent-genauen Ratenplan der Zeile über den geteilten
     * {@link computeRatePlan}-Util (Einzelraten, Fälligkeiten, Summe,
     * Plausibilität). Die Fälligkeiten starten heute + 1 Monat im Monats-Intervall
     * — analog zum Ratenvereinbarungs-Dialog.
     */
    ratePlan(row: BdContractDecisionRow): BdRatePlan {
        const total = this.ratePlanTotal(row);
        const count = row.rateAgreement.monthsCount ?? 0;
        return computeRatePlan(total, count, this.ratePlanStartDate(), 'MONTHLY');
    }

    /**
     * G10: Convenience-Zugriff auf die Einzelraten der Zeile für die Vorschau.
     */
    ratePlanInstallments(row: BdContractDecisionRow): BdRateAgreementInstallment[] {
        return this.ratePlan(row).installments;
    }

    /**
     * G10: true, wenn die Ratenplanung NICHT cent-genau zum Gesamtbetrag passt.
     * Nutzt die exakte {@code plausibilityOk}-Prüfung aus dem geteilten Util.
     */
    isRatePlanImplausible(row: BdContractDecisionRow): boolean {
        if (row.selectedAction !== 'RATE_AGREEMENT') {
            return false;
        }
        const count = row.rateAgreement.monthsCount ?? 0;
        if (count <= 0 || this.ratePlanTotal(row) <= 0) {
            return false;
        }
        return !this.ratePlan(row).plausibilityOk;
    }

    /**
     * G10: Standard-Startdatum der ersten Rate (heute + 1 Monat). Mirror des
     * Ratenvereinbarungs-Dialogs, damit die Fälligkeiten konsistent sind.
     */
    private ratePlanStartDate(): Date {
        const date = new Date();
        date.setMonth(date.getMonth() + 1);
        return date;
    }

    /**
     * Formatiert ein Date-Objekt als ISO-Datum (yyyy-MM-dd) für das Backend.
     */
    private toIsoDate(date: Date): string {
        const yyyy = date.getFullYear();
        const mm = `${date.getMonth() + 1}`.padStart(2, '0');
        const dd = `${date.getDate()}`.padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    /**
     * German plain-text label for an action, reused in the confirm dialog (C05).
     *
     * @ticket TM-Wave-B
     */
    getActionLabel(action: BdContractDecisionAction | null): string {
        switch (action) {
            case 'HANDOVER':         return 'Inkasso-Übergabe';
            case 'WRITE_OFF':        return 'Abschreibung';
            case 'SPECIAL_HANDLING': return 'Sonderbehandlung';
            case 'RATE_AGREEMENT':   return 'Ratenzahlung';
            case 'REACTIVATE':       return 'Vertrag wieder in Kraft setzen';
            case 'STORNO':           return 'Vertrag stornieren';
            default:                 return '—';
        }
    }

    /**
     * Returns true when the row can be submitted (option chosen + not already
     * submitted). For irreversible actions a non-empty reason is mandatory (C05);
     * the "Auswahl bestätigen" button stays disabled until a reason is entered.
     *
     * @ticket TM-Wave-B
     */
    canSubmit(row: BdContractDecisionRow): boolean {
        if (row.submitted || row.submitting || row.selectedAction == null) {
            return false;
        }
        if (row.selectedAction === 'RATE_AGREEMENT') {
            // G10: Plausibel ist ein Plan mit Laufzeit > 0 und positivem Gesamtbetrag.
            return (row.rateAgreement.monthsCount ?? 0) > 0
                && this.ratePlanTotal(row) > 0;
        }
        if (row.selectedAction === 'REACTIVATE') {
            // G5: reversibel, aber Pflicht-Begründung; Stichtag ist optional.
            return this.effectiveReason(row).length > 0;
        }
        if (row.selectedAction === 'STORNO') {
            // G6: irreversibel — Pflicht-Begründung UND Pflicht-Stichtag.
            return this.effectiveReason(row).length > 0
                && row.stornoEffectiveDate != null;
        }
        if (this.isIrreversible(row.selectedAction)) {
            return this.effectiveReason(row).length > 0;
        }
        return true;
    }

    // -------------------------------------------------------------------- //
    // Internal helpers
    // -------------------------------------------------------------------- //

    /**
     * Maps the raw contract input into the local row state.
     * If the parent has not provided contracts, an empty array is returned so the
     * empty-state message is shown to the user.
     */
    private buildRows(contracts: any[] | null): BdContractDecisionRow[] {
        if (!contracts || contracts.length === 0) {
            return [];
        }
        return contracts.map(c => ({
            contractId: c?.id ?? c?.contractId ?? 0,
            contractName: c?.identifier ?? c?.contractName ?? `Vertrag ${c?.id ?? '?'}`,
            dunningBalance: c?.dunningBalance ?? c?.balance ?? c?.mahnRelevantBalance ?? 0,
            agentHint: !!c?.agentHint,
            agentNumber: c?.agentNumber != null ? `${c.agentNumber}` : null,
            isLvContract: !!c?.isLvContract,
            selectedAction: null,
            submitting: false,
            submitted: false,
            rateAgreement: {
                totalAmount: null,
                monthlyAmount: null,
                monthsCount: null,
                showDialog: false
            },
            writeOff: {amount: null, reason: null},
            reactivateEffectiveDate: null,
            stornoEffectiveDate: null,
            reason: ''
        }));
    }

    private extractError(err: unknown): string {
        if (err && typeof err === 'object') {
            const anyErr = err as any;
            return anyErr.error?.message ?? anyErr.message ?? 'Unbekannter Fehler.';
        }
        return 'Unbekannter Fehler.';
    }
}
